// frontend/src/components/employee/availability/EmployeeAvailability.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import {
  createAvailabilityBlock,
  deleteAvailabilityBlock,
  fetchTutorBlocksInRange,
  fetchTutorPayrollSegmentsInRange,
  updateAvailabilityBlock,
} from "./availabilityFirestore";
import { fetchDayConfigsForDates, fetchScheduledSessions } from "../../scheduling/schedulingApi";
import AvailabilityModal from "./AvailabilityModal";
import {
  addMonths,
  atTime,
  endOfMonthExclusive,
  isDisabledDay,
  isSameMonth,
  monthGridDays,
  resolveDayTimelineHours,
  sameYMD,
  startOfMonth,
  ymd,
} from "./availabilityUtils";
import { isRejectedStatus } from "../../../../utils/userStatus";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAST_INIT   = 2;
const FUTURE_INIT = 4;
const LOAD_PAST   = 3;
const LOAD_FUTURE = 3;

const CALENDAR_MODE_OPTIONS = [
  { value: "availability", label: "Availability" },
  { value: "scheduled",   label: "Scheduled Hours" },
  { value: "clock",       label: "Clock In/Out" },
  { value: "teaching",    label: "Teaching Hours" },
  { value: "admin",       label: "Admin Hours" },
];

// Tailwind 400/500-level borders for visibility; very light tinted backgrounds; dark text for contrast
const MODE_META = {
  availability: { label: "Availability",    cardBg: "#f1f5f9", cardBorder: "#cbd5e1", cardText: "#334155" },
  clock:        { label: "Clock In/Out",    cardBg: "#f4f4f5", cardBorder: "#e4e4e7", cardText: "#18181b" },
  teaching:     { label: "Teaching Hours",  cardBg: "#f0fdf4", cardBorder: "#22c55e", cardText: "#14532d" },
  admin:        { label: "Admin Hours",     cardBg: "#eff6ff", cardBorder: "#3b82f6", cardText: "#1e3a8a" },
  scheduled:    { label: "Scheduled Hours", cardBg: "#f5f3ff", cardBorder: "#ddd6fe", cardText: "#4c1d95" },
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function toDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  return ts instanceof Date ? ts : new Date(ts);
}

function fmtTime(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "--";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function fmtHours(mins) {
  const v = mins / 60;
  if (!isFinite(v)) return "0";
  return v.toFixed(2).replace(/\.?0+$/, "");
}

function monthKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}`;
}

function isCancelled(status) {
  const v = String(status || "").trim().toLowerCase();
  return v === "cancelled" || v === "canceled";
}

/** Snapshot the first [data-month-key] node whose bottom edge is below the container top. */
function captureAnchor(el) {
  if (!el) return null;
  const cRect = el.getBoundingClientRect();
  const nodes = el.querySelectorAll("[data-month-key]");
  for (const node of nodes) {
    const r = node.getBoundingClientRect();
    if (r.bottom > cRect.top) {
      return { key: node.getAttribute("data-month-key"), top: r.top - cRect.top };
    }
  }
  return null;
}

function isWheelIsolatedTarget(target) {
  return !!(
    target &&
    typeof target.closest === "function" &&
    target.closest("[data-wheel-isolated='true']")
  );
}

function HeaderSelect({
  value,
  onChange,
  options,
  placeholder,
  minWidth,
  disabled = false,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = options.find(opt => opt.value === value);
  const label = selected?.label || placeholder || "";

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  return (
    <div ref={rootRef} style={{ ...CS.selectWrap, minWidth }}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => !disabled && setOpen(prev => !prev)}
        style={CS.selectTrigger}
      >
        <span style={{ ...CS.selectValue, color: selected ? "#374151" : "#9ca3af" }}>{label}</span>
        <ChevronDown size={15} color="#6b7280" />
      </button>

      {open && (
        <div role="listbox" data-wheel-isolated="true" style={CS.selectMenu}>
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  ...CS.selectItem,
                  background: isSelected ? "#f3f4f6" : "transparent",
                  color: "#111827",
                }}
              >
                <span style={{ paddingRight: 20 }}>{opt.label}</span>
                {isSelected && <Check size={15} color="#111827" style={{ flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── DayCell ──────────────────────────────────────────────────────────────────

function DayCell({
  date, month, idx, today,
  calendarMode, meta,
  blocksByDay, payrollByDay, scheduledMinsByDay,
  dayConfigsMap, selectedDate,
  isAvMode, isStackedMode, isScheduledMode,
  onDayClick, onDayDoubleClick,
  onBlockDoubleClick, onBlockDragStart, onCellDrop,
  openDetailModal,
}) {
  const inMonth  = isSameMonth(date, month);
  const hasPrevInMonth = idx > 0 && isSameMonth(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1), month);
  const hasAboveInMonth = isSameMonth(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7), month);

  // Out-of-month cells: visually blank/inactive to keep focus on current month
  if (!inMonth) {
    return (
      <div style={{
        height: 100,
        background: "#fff",
        borderBottom: "none",
        borderRight:  "none",
        pointerEvents: "none",
      }} />
    );
  }

  const key      = ymd(date);
  const disabled = isDisabledDay(date, dayConfigsMap);
  const isToday  = sameYMD(date, today);
  const isSel    = isAvMode && selectedDate && sameYMD(selectedDate, date);

  let segs = [];
  if      (calendarMode === "availability") segs = (blocksByDay.get(key) || []).map(b => ({ id: b.id, start: toDate(b.start), end: toDate(b.end), block: b }));
  else if (calendarMode === "clock")        segs = payrollByDay.clockByDay.get(key)    || [];
  else if (calendarMode === "teaching")     segs = payrollByDay.teachingByDay.get(key) || [];
  else if (calendarMode === "admin")        segs = payrollByDay.adminByDay.get(key)    || [];

  const scheduledMins = scheduledMinsByDay.get(key) || 0;
  const visible       = isStackedMode ? segs.slice(0, 2) : segs;
  const hiddenCount   = isStackedMode ? Math.max(0, segs.length - 2) : 0;

  const cellBg = isSel    ? "#f0f4ff"
    : disabled             ? "#f0f2f5"
    : "#fff";

  return (
    <div
      onClick={(e) => onDayClick(e, date, segs, disabled)}
      onDoubleClick={() => onDayDoubleClick(date)}
      onDragOver={(e) => { if (isAvMode && !disabled) e.preventDefault(); }}
      onDrop={(e)     => { if (isAvMode && !disabled) onCellDrop(e, date); }}
      style={{
        height: 100,
        background: cellBg,
        borderLeft: hasPrevInMonth ? "none" : "1px solid #e5e7eb",
        borderTop: hasAboveInMonth ? "none" : "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
        borderRight:  isScheduledMode && idx === 6 ? "none" : "1px solid #e5e7eb",
        padding: "6px 7px 5px",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        cursor: isAvMode ? (disabled ? "default" : "pointer")
          : isStackedMode && segs.length > 0 ? "pointer"
          : "default",
        boxShadow: isSel ? "inset 0 0 0 1.5px #1f2937" : "none",
        transition: "background-color 0.1s",
      }}
    >
      {/* Date number — fixed 24px height so today's circle doesn't push content down */}
      <div style={{
        alignSelf: "flex-end",
        height: 24,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        marginBottom: 4,
        flexShrink: 0,
      }}>
        <div style={{
          width: isToday ? 22 : "auto",
          height: isToday ? 22 : "auto",
          borderRadius: "50%",
          background: isToday ? "#1f2937" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isToday ? 0 : "0 2px",
        }}>
          <span style={{
            fontSize: 12,
            fontWeight: isToday ? 700 : 500,
            color: isToday    ? "#fff"
              : disabled      ? "#c9cdd4"
              : "#374151",
            lineHeight: 1,
          }}>
            {date.getDate()}
          </span>
        </div>
      </div>

      {/* Cell content */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>

        {/* Scheduled: hours bubble */}
        {isScheduledMode && scheduledMins > 0 && (
          <div style={{
            background: meta.cardBg,
            border: `1px solid ${meta.cardBorder}`,
            borderRadius: 7,
            padding: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: meta.cardText }}>
              {fmtHours(scheduledMins)}h
            </span>
          </div>
        )}

        {/* Availability / clock: time range blocks */}
        {!isStackedMode && !isScheduledMode && visible.map(seg => (
          <div
            key={seg.id}
            draggable={isAvMode && !disabled}
            onDragStart={(e) => { if (isAvMode && seg.block) onBlockDragStart(e, seg.block); }}
            onDoubleClick={(e) => { if (isAvMode && seg.block) onBlockDoubleClick(e, date, seg.block); }}
            style={{
              background: meta.cardBg,
              border: `1px solid ${meta.cardBorder}`,
              borderRadius: 6,
              padding: "4px 5px",
              cursor: isAvMode && !disabled ? "grab" : "default",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 42,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: meta.cardText, lineHeight: 1.3 }}>
              {fmtTime(seg.start)}
            </span>
            <div style={{ width: 18, height: 1, background: meta.cardBorder, margin: "2px 0", opacity: 0.6 }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: meta.cardText, lineHeight: 1.3 }}>
              {fmtTime(seg.end)}
            </span>
          </div>
        ))}

        {/* Teaching / Admin: left-border stripe pills */}
        {isStackedMode && visible.map(seg => (
          <div
            key={seg.id}
            style={{
              background: meta.cardBg,
              borderLeft: `3px solid ${meta.cardBorder}`,
              borderRadius: "0 4px 4px 0",
              padding: "3px 6px 3px 5px",
              fontSize: 11,
              fontWeight: 600,
              color: meta.cardText,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`${fmtTime(seg.start)} – ${fmtTime(seg.end)}`}
          >
            {fmtTime(seg.start)} – {fmtTime(seg.end)}
          </div>
        ))}

        {isStackedMode && hiddenCount > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openDetailModal(date, segs); }}
            style={{
              border: "none", background: "none",
              color: "#9ca3af", fontSize: 11, fontWeight: 500,
              textAlign: "left", padding: 0, cursor: "pointer",
            }}
          >
            +{hiddenCount} more
          </button>
        )}
      </div>
    </div>
  );
}

// ─── MonthSection ─────────────────────────────────────────────────────────────
// Renders week-by-week. Skips entirely-out-of-month rows (trims the grid).
// In scheduled mode, appends an 8th "weekly total" column to each row.

function MonthSection({ month, monthRef, isCurrentMonth, ...cellProps }) {
  const { today, isScheduledMode, scheduledMinsByDay } = cellProps;
  const days = useMemo(() => monthGridDays(month), [month]);

  // Build week rows, skipping rows with zero in-month days
  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      const week = days.slice(i, i + 7);
      if (week.some(d => isSameMonth(d, month))) rows.push(week);
    }
    return rows;
  }, [days, month]);

  const gridCols = isScheduledMode ? "repeat(7, minmax(106px, 1fr)) 84px" : "repeat(7, minmax(106px, 1fr))";

  return (
    <div data-month-key={monthKey(month)} ref={monthRef} style={{ scrollMarginTop: 38 }}>

      {/* Month heading */}
      <div style={{
        display: "flex",
        alignItems: "baseline",
        gap: 10,
        padding: "26px 0 12px",
      }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" }}>
          {month.toLocaleDateString("en-US", { month: "long" })}
        </span>
        <span style={{ fontSize: 13, fontWeight: 400, color: "#9ca3af", letterSpacing: 0 }}>
          {month.getFullYear()}
        </span>
        {isCurrentMonth && (
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "#6b7280",
            border: "1px solid #d1d5db",
            borderRadius: 100,
            padding: "2px 8px",
            lineHeight: 1,
          }}>
            This Month
          </span>
        )}
      </div>

      {/* Week rows */}
      {weeks.map((week, wi) => {
        const weekTotal = isScheduledMode
          ? week
              .filter(d => isSameMonth(d, month))
              .reduce((s, d) => s + (scheduledMinsByDay.get(ymd(d)) || 0), 0)
          : 0;

        return (
          <div
            key={wi}
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
            }}
          >
            {week.map((date, idx) => (
              <DayCell
                key={`${ymd(date)}-${idx}`}
                date={date}
                month={month}
                idx={idx}
                today={today}
                {...cellProps}
              />
            ))}

            {/* Weekly total cell — scheduled mode only */}
            {isScheduledMode && (
              <div style={{
                height: 100,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderTop: wi === 0 ? "1px solid #e5e7eb" : "none",
                borderBottom: "1px solid #e5e7eb",
                borderLeft: "1px solid #d1d5db",
                background: "#f5f7fa",
                padding: "6px 4px",
                gap: 2,
              }}>
                {weekTotal > 0 ? (
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#374151", lineHeight: 1 }}>
                    {fmtHours(weekTotal)}h
                  </span>
                ) : (
                  <span style={{ fontSize: 13, color: "#d1d5db" }}>—</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EmployeeAvailability({ employees }) {
  // ── Filter state ──
  const [selectedRole,    setSelectedRole]    = useState("tutor");
  const [selectedStatus,  setSelectedStatus]  = useState("active");
  const [selectedTutorId, setSelectedTutorId] = useState("");
  const [calendarMode,    setCalendarMode]    = useState("availability");

  // ── Multi-month state ──
  const BASE  = useMemo(() => startOfMonth(new Date()), []);
  const today = useMemo(() => new Date(), []);

  const [months,    setMonths]    = useState(() =>
    Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, i) => addMonths(BASE, i - PAST_INIT))
  );
  const [pastCount, setPastCount] = useState(PAST_INIT);

  // ── Data state ──
  const [blocks,            setBlocks]           = useState([]);
  const [payrollRows,       setPayrollRows]       = useState([]);
  const [scheduledSessions, setScheduledSessions] = useState([]);
  const [dayConfigsMap,     setDayConfigsMap]     = useState({});
  const [isLoadingMore,     setIsLoadingMore]     = useState(false);

  // ── Calendar interaction ──
  const [selectedDate, setSelectedDate] = useState(null);
  const [clipboard,    setClipboard]    = useState(null);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [modalDate,    setModalDate]    = useState(null);
  const [editBlock,    setEditBlock]    = useState(null);
  const [detailModal,  setDetailModal]  = useState(null);

  // ── Refs ──
  const containerRef     = useRef(null);
  const currentMonthRef  = useRef(null);
  const isLoadingPastRef = useRef(false);
  const isLoadingFutRef  = useRef(false);
  const loadedStartRef   = useRef(-PAST_INIT);
  const loadedEndRef     = useRef(FUTURE_INIT);
  const epochRef         = useRef(0);

  // ── Derived ──
  const isAvMode        = calendarMode === "availability";
  const isStackedMode   = calendarMode === "teaching" || calendarMode === "admin";
  const isScheduledMode = calendarMode === "scheduled";
  const meta            = MODE_META[calendarMode] || MODE_META.availability;

  const tutors = useMemo(() => {
    return (employees || [])
      .filter(u => {
        const role   = String(u.role   || "tutor").toLowerCase();
        const status = String(u.status || "active").toLowerCase();
        if (isRejectedStatus(status)) return false;
        if (selectedRole   !== "all" && role   !== selectedRole)               return false;
        if (selectedStatus !== "all" && status !== selectedStatus.toLowerCase()) return false;
        return true;
      })
      .map(u => ({ id: u.id, name: u.name || "(Tutor)" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, selectedRole, selectedStatus]);

  useEffect(() => {
    if (selectedTutorId && !tutors.some(t => t.id === selectedTutorId)) {
      setSelectedTutorId("");
    }
  }, [tutors, selectedTutorId]);

  useEffect(() => {
    if (!isAvMode) {
      setSelectedDate(null);
      setClipboard(null);
      setModalOpen(false);
      setEditBlock(null);
      setModalDate(null);
    }
  }, [calendarMode, isAvMode]);

  // ── Data loading ──
  async function loadRange(fromOffset, toOffset, tutorId) {
    const rangeMonths = Array.from(
      { length: toOffset - fromOffset + 1 },
      (_, i) => addMonths(BASE, fromOffset + i)
    );
    const allDays = rangeMonths.flatMap(m => monthGridDays(m));
    const start   = allDays[0];
    const end     = endOfMonthExclusive(rangeMonths[rangeMonths.length - 1]);

    if (!tutorId) {
      const cfgs = await fetchDayConfigsForDates(allDays);
      return { cfgs, avail: [], payroll: [], sched: [] };
    }

    const [cfgs, avail, payroll, sched] = await Promise.all([
      fetchDayConfigsForDates(allDays),
      fetchTutorBlocksInRange(tutorId, start, end),
      fetchTutorPayrollSegmentsInRange(tutorId, start, end),
      fetchScheduledSessions({ rangeStart: start, rangeEnd: end, tutorUid: tutorId }),
    ]);
    return { cfgs, avail, payroll, sched: sched || [] };
  }

  useEffect(() => {
    epochRef.current += 1;
    const epoch = epochRef.current;

    const initMonths = Array.from(
      { length: PAST_INIT + 1 + FUTURE_INIT },
      (_, i) => addMonths(BASE, i - PAST_INIT)
    );
    setMonths(initMonths);
    setPastCount(PAST_INIT);
    loadedStartRef.current = -PAST_INIT;
    loadedEndRef.current   = FUTURE_INIT;
    setBlocks([]);
    setPayrollRows([]);
    setScheduledSessions([]);
    setDayConfigsMap({});
    setSelectedDate(null);
    setClipboard(null);

    setTimeout(() => {
      currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }, 80);

    (async () => {
      try {
        const { cfgs, avail, payroll, sched } = await loadRange(-PAST_INIT, FUTURE_INIT, selectedTutorId);
        if (epochRef.current !== epoch) return;
        setDayConfigsMap(cfgs);
        setBlocks(avail);
        setPayrollRows(payroll);
        setScheduledSessions(sched);
      } catch (e) {
        console.error(e);
        if (epochRef.current !== epoch) return;
        alert("Failed to load calendar data.");
      }
    })();
  }, [selectedTutorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreFuture = useCallback(async () => {
    if (isLoadingFutRef.current) return;
    isLoadingFutRef.current = true;
    setIsLoadingMore(true);

    const prevEnd   = loadedEndRef.current;
    const newEnd    = prevEnd + LOAD_FUTURE;
    const newMonths = Array.from({ length: LOAD_FUTURE }, (_, i) => addMonths(BASE, prevEnd + 1 + i));

    try {
      const { cfgs, avail, payroll, sched } = await loadRange(prevEnd + 1, newEnd, selectedTutorId);
      setDayConfigsMap(p => ({ ...p, ...cfgs }));
      setBlocks(p        => { const seen = new Set(p.map(b => b.id)); return [...p, ...avail.filter(b => !seen.has(b.id))]; });
      setPayrollRows(p   => { const seen = new Set(p.map(r => r.id)); return [...p, ...payroll.filter(r => !seen.has(r.id))]; });
      setScheduledSessions(p => { const seen = new Set(p.map(s => s.id)); return [...p, ...sched.filter(s => !seen.has(s.id))]; });
      setMonths(p => [...p, ...newMonths]);
      loadedEndRef.current = newEnd;
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingFutRef.current = false;
      setIsLoadingMore(false);
    }
  }, [BASE, selectedTutorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMorePast = useCallback(async () => {
    if (isLoadingPastRef.current) return;
    isLoadingPastRef.current = true;

    const prevStart = loadedStartRef.current;
    const newStart  = prevStart - LOAD_PAST;
    const newMonths = Array.from({ length: LOAD_PAST }, (_, i) => addMonths(BASE, newStart + i));

    try {
      const { cfgs, avail, payroll, sched } = await loadRange(newStart, prevStart - 1, selectedTutorId);

      const el = containerRef.current;
      if (!el) return;
      const anchor = captureAnchor(el);

      flushSync(() => {
        setDayConfigsMap(p => ({ ...p, ...cfgs }));
        setBlocks(p        => { const seen = new Set(p.map(b => b.id)); return [...avail.filter(b => !seen.has(b.id)), ...p]; });
        setPayrollRows(p   => { const seen = new Set(p.map(r => r.id)); return [...payroll.filter(r => !seen.has(r.id)), ...p]; });
        setScheduledSessions(p => { const seen = new Set(p.map(s => s.id)); return [...sched.filter(s => !seen.has(s.id)), ...p]; });
        setMonths(p => [...newMonths, ...p]);
        setPastCount(p => p + LOAD_PAST);
        loadedStartRef.current = newStart;
      });

      if (anchor?.key) {
        requestAnimationFrame(() => {
          const cRect      = el.getBoundingClientRect();
          const anchorNode = el.querySelector(`[data-month-key="${anchor.key}"]`);
          if (!anchorNode) return;
          el.scrollTop += anchorNode.getBoundingClientRect().top - cRect.top - anchor.top;
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingPastRef.current = false;
    }
  }, [BASE, selectedTutorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      if (el.scrollTop < 400)                                      loadMorePast();
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 400)  loadMoreFuture();
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadMorePast, loadMoreFuture]);

  // ── Computed maps ──
  const blocksByDay = useMemo(() => {
    const map = new Map();
    for (const b of blocks) {
      const s = toDate(b.start);
      if (!s || isNaN(s.getTime())) continue;
      const k = ymd(s);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(b);
    }
    for (const [k, arr] of map) { arr.sort((a, b) => toDate(a.start) - toDate(b.start)); map.set(k, arr); }
    return map;
  }, [blocks]);

  const payrollByDay = useMemo(() => {
    const all = new Map(), teaching = new Map(), admin = new Map();
    for (const row of payrollRows) {
      const s = toDate(row.startTime), e = toDate(row.endTime);
      if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) continue;
      const k    = ymd(s);
      const type = String(row.type || "").trim().toLowerCase();
      const seg  = { id: row.id, start: s, end: e, type };
      if (!all.has(k)) all.set(k, []); all.get(k).push(seg);
      if (type === "teaching") { if (!teaching.has(k)) teaching.set(k, []); teaching.get(k).push(seg); }
      if (type === "admin")    { if (!admin.has(k))    admin.set(k, []);    admin.get(k).push(seg); }
    }
    const sort = m => { for (const [k, a] of m) { a.sort((x, y) => x.start - y.start); m.set(k, a); } };
    sort(all); sort(teaching); sort(admin);
    const clock = new Map();
    for (const [k, arr] of all) {
      if (!arr.length) continue;
      let end = arr[0].end;
      for (const s of arr) { if (s.end > end) end = s.end; }
      clock.set(k, [{ id: `clock-${k}`, start: arr[0].start, end, type: "clock" }]);
    }
    return { clockByDay: clock, teachingByDay: teaching, adminByDay: admin };
  }, [payrollRows]);

  const scheduledMinsByDay = useMemo(() => {
    const intervals = new Map();
    for (const session of scheduledSessions) {
      if (isCancelled(session?.status)) continue;
      const s = toDate(session?.start), e = toDate(session?.end);
      if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) continue;
      const ds = new Date(s); ds.setHours(0, 0, 0, 0);
      const de = new Date(ds); de.setDate(de.getDate() + 1);
      const cs = s < ds ? ds : s, ce = e > de ? de : e;
      if (ce <= cs) continue;
      const k = ymd(cs);
      if (!intervals.has(k)) intervals.set(k, []);
      intervals.get(k).push({ start: cs, end: ce });
    }
    const mins = new Map();
    for (const [k, arr] of intervals) {
      const sorted = [...arr].sort((a, b) => a.start - b.start);
      const merged = [];
      for (const iv of sorted) {
        const prev = merged[merged.length - 1];
        if (!prev || iv.start > prev.end) { merged.push({ ...iv }); continue; }
        if (iv.end > prev.end) prev.end = iv.end;
      }
      mins.set(k, Math.max(0, Math.round(merged.reduce((s, x) => s + (x.end - x.start), 0) / 60000)));
    }
    return mins;
  }, [scheduledSessions]);

  // ── Availability interactions ──
  const openDetailModal = useCallback((date, segs) => {
    if (!isStackedMode || !segs?.length) return;
    setDetailModal({ date, mode: calendarMode, segs: [...segs].sort((a, b) => a.start - b.start) });
  }, [calendarMode, isStackedMode]);

  const handleDayClick = (e, date, segs, disabled) => {
    e.stopPropagation();
    if (isAvMode) {
      if (disabled) { setSelectedDate(null); return; }
      setSelectedDate(prev => prev && sameYMD(prev, date) ? null : date);
      return;
    }
    if (isStackedMode && segs.length > 0) openDetailModal(date, segs);
  };

  const handleDayDoubleClick = (date) => {
    if (!isAvMode || !date || isDisabledDay(date, dayConfigsMap) || !selectedTutorId) return;
    setEditBlock(null);
    setModalDate(date);
    setModalOpen(true);
  };

  const handleBlockDoubleClick = (e, date, block) => {
    e.stopPropagation();
    if (!isAvMode || !date) return;
    setEditBlock(block);
    setModalDate(date);
    setModalOpen(true);
  };

  const handleBlockDragStart = (e, block) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: block.id }));
  };

  const handleCellDrop = (e, date) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    try {
      const { id } = JSON.parse(raw);
      const block = blocks.find(b => b.id === id);
      if (block) moveBlock(block, date);
    } catch {}
  };

  const handleCopy = useCallback(() => {
    if (!isAvMode || !selectedDate) return;
    const dayBlocks = blocksByDay.get(ymd(selectedDate)) || [];
    if (!dayBlocks.length) { setClipboard(null); return; }
    setClipboard(dayBlocks.map(b => ({
      startMin: toDate(b.start).getHours() * 60 + toDate(b.start).getMinutes(),
      endMin:   toDate(b.end).getHours() * 60 + toDate(b.end).getMinutes(),
    })));
  }, [selectedDate, blocksByDay, isAvMode]);

  const handlePaste = useCallback(async () => {
    if (!isAvMode || !selectedDate || !clipboard?.length ||
        isDisabledDay(selectedDate, dayConfigsMap) || !selectedTutorId) return;
    const existing = blocksByDay.get(ymd(selectedDate)) || [];
    const toCreate = clipboard
      .filter(item => !existing.some(b =>
        toDate(b.start).getHours() * 60 + toDate(b.start).getMinutes() === item.startMin &&
        toDate(b.end).getHours() * 60 + toDate(b.end).getMinutes() === item.endMin
      ))
      .map(item => ({
        startDate: atTime(selectedDate, item.startMin),
        endDate:   atTime(selectedDate, item.endMin),
      }));
    if (!toCreate.length) return;
    try {
      const created = [];
      for (const nb of toCreate) {
        const id = await createAvailabilityBlock({ uid: selectedTutorId, ...nb });
        created.push({ id, uid: selectedTutorId, start: nb.startDate, end: nb.endDate });
      }
      setBlocks(p => [...p, ...created]);
    } catch (e) { console.error(e); alert("Paste failed."); }
  }, [selectedDate, clipboard, blocksByDay, selectedTutorId, dayConfigsMap, isAvMode]);

  const handleDeleteKey = useCallback(async () => {
    if (!isAvMode || !selectedDate || isDisabledDay(selectedDate, dayConfigsMap)) return;
    const dayBlocks = blocksByDay.get(ymd(selectedDate)) || [];
    if (!dayBlocks.length) return;
    if (!window.confirm(`Delete all availability for ${selectedDate.toDateString()}?`)) return;
    try {
      await Promise.all(dayBlocks.map(b => deleteAvailabilityBlock(b.id)));
      setBlocks(p => p.filter(b => !dayBlocks.includes(b)));
    } catch (e) { console.error(e); alert("Delete failed."); }
  }, [selectedDate, blocksByDay, dayConfigsMap, isAvMode]);

  useEffect(() => {
    const handler = e => {
      if (!isAvMode || !selectedDate || modalOpen) return;
      if ((e.metaKey || e.ctrlKey) && e.key === "c") { e.preventDefault(); handleCopy(); }
      if ((e.metaKey || e.ctrlKey) && e.key === "v") { e.preventDefault(); handlePaste(); }
      if (e.key === "Backspace" || e.key === "Delete") { e.preventDefault(); handleDeleteKey(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedDate, modalOpen, handleCopy, handlePaste, handleDeleteKey, isAvMode]);

  async function saveModal({ startMin, endMin }) {
    if (!isAvMode || !selectedTutorId || !modalDate) return;
    const bounds = resolveDayTimelineHours(modalDate, dayConfigsMap);
    if (bounds.isClosed) { alert("This day is closed in schedule settings."); return; }
    const safeS = Math.max(bounds.startMin, Math.min(bounds.endMin - 30, startMin));
    const safeE = Math.min(bounds.endMin, Math.max(safeS + 30, endMin));
    if (safeE <= safeS) { alert("Selected time is outside the configured schedule range."); return; }
    const sd = atTime(modalDate, safeS), ed = atTime(modalDate, safeE);
    try {
      if (editBlock) {
        await updateAvailabilityBlock(editBlock.id, { startDate: sd, endDate: ed });
        setBlocks(p => p.map(b => b.id === editBlock.id ? { ...b, start: sd, end: ed } : b));
      } else {
        const id = await createAvailabilityBlock({ uid: selectedTutorId, startDate: sd, endDate: ed });
        setBlocks(p => [...p, { id, uid: selectedTutorId, start: sd, end: ed }]);
      }
      setModalOpen(false); setEditBlock(null); setModalDate(null);
    } catch (e) { console.error(e); alert("Save failed: " + e.message); }
  }

  async function deleteModal() {
    if (!editBlock) return;
    try {
      await deleteAvailabilityBlock(editBlock.id);
      setBlocks(p => p.filter(b => b.id !== editBlock.id));
      setModalOpen(false); setEditBlock(null); setModalDate(null);
    } catch (e) { console.error(e); alert("Delete failed."); }
  }

  async function moveBlock(block, targetDate) {
    if (!isAvMode || !block || !targetDate || isDisabledDay(targetDate, dayConfigsMap)) return;
    const s = toDate(block.start), e = toDate(block.end);
    const sd = atTime(targetDate, s.getHours() * 60 + s.getMinutes());
    const ed = atTime(targetDate, e.getHours() * 60 + e.getMinutes());
    try {
      await updateAvailabilityBlock(block.id, { startDate: sd, endDate: ed });
      setBlocks(p => p.map(b => b.id === block.id ? { ...b, start: sd, end: ed } : b));
    } catch { alert("Move failed."); }
  }

  const scrollToToday = () => {
    currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const handleOuterWheel = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;
    if (isWheelIsolatedTarget(e.target)) return;
    if (container.contains(e.target)) return;
    if (Math.abs(e.deltaY) < 1) return;

    container.scrollTop += e.deltaY;
    e.preventDefault();
  }, []);

  const modalBounds = modalDate ? resolveDayTimelineHours(modalDate, dayConfigsMap) : null;

  const cellProps = {
    calendarMode, meta,
    blocksByDay, payrollByDay, scheduledMinsByDay,
    dayConfigsMap, selectedDate,
    isAvMode, isStackedMode, isScheduledMode,
    today,
    onDayClick:         handleDayClick,
    onDayDoubleClick:   handleDayDoubleClick,
    onBlockDoubleClick: handleBlockDoubleClick,
    onBlockDragStart:   handleBlockDragStart,
    onCellDrop:         handleCellDrop,
    openDetailModal,
  };

  const weekdayCols = isScheduledMode ? "repeat(7, minmax(106px, 1fr)) 84px" : "repeat(7, minmax(106px, 1fr))";
  const calendarMinWidth = isScheduledMode ? 860 : 760;

  // ── Render ──
  return (
    <div
      onClick={() => { if (isAvMode) setSelectedDate(null); }}
      onWheel={handleOuterWheel}
      style={{
        height: "100%", width: "100%", background: "#fff",
      }}
    >
      <div style={{
        height: "100%", display: "flex", flexDirection: "column", background: "#fff",
        maxWidth: "var(--cal-page-max-width, 1440px)", width: "100%", margin: "0 auto",
      }}>
      {/* ── Compact unified header ── */}
      <div style={{
        flexShrink: 0,
        background: "#fff",
        borderBottom: "1px solid #e5e7eb",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}>
        {/* Left: filter controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <HeaderSelect
            value={selectedRole}
            onChange={(next) => { setSelectedRole(next); setSelectedTutorId(""); }}
            options={[
              { value: "tutor", label: "Tutor" },
              { value: "admin", label: "Admin" },
              { value: "all", label: "All Roles" },
            ]}
            ariaLabel="Role"
            minWidth={94}
          />

          <HeaderSelect
            value={selectedStatus}
            onChange={(next) => { setSelectedStatus(next); setSelectedTutorId(""); }}
            options={[
              { value: "all", label: "All Status" },
              { value: "active", label: "Active" },
              { value: "finished", label: "Finished" },
              { value: "on hold", label: "On Hold" },
            ]}
            ariaLabel="Status"
            minWidth={104}
          />

          {/* Vertical divider */}
          <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 2px", flexShrink: 0 }} />

          <HeaderSelect
            value={selectedTutorId}
            onChange={setSelectedTutorId}
            options={tutors.map(t => ({ value: t.id, label: t.name }))}
            placeholder="Select employee…"
            ariaLabel="Employee"
            minWidth={168}
          />
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Right: clipboard indicator, view mode, today */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isAvMode && clipboard && (
            <span style={{
              fontSize: 11,
              fontWeight: 500,
              color: "#6b7280",
              background: "#f3f4f6",
              borderRadius: 6,
              padding: "4px 9px",
              whiteSpace: "nowrap",
            }}>
              {clipboard.length} block{clipboard.length !== 1 ? "s" : ""} copied
            </span>
          )}

          <HeaderSelect
            value={calendarMode}
            onChange={setCalendarMode}
            options={CALENDAR_MODE_OPTIONS}
            ariaLabel="Mode"
            minWidth={144}
          />

          <button onClick={scrollToToday} style={CS.todayBtn}>Today</button>
        </div>
      </div>

      {/* ── Calendar ── */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div
          ref={containerRef}
          style={{ height: "100%", overflowY: "auto", overflowX: "auto", background: "#fff" }}
        >
          {/* Sticky weekday header */}
          <div style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "#fff",
            borderBottom: "1px solid #e5e7eb",
            minWidth: calendarMinWidth,
          }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: weekdayCols,
              padding: "0 24px",
            }}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                <div key={d} style={{
                  padding: "8px 4px",
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  color: i === 0 || i === 6 ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}>
                  {d}
                </div>
              ))}
              {isScheduledMode && (
                <div style={{
                  padding: "8px 4px",
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  borderLeft: "1px solid #d1d5db",
                }}>
                  Total
                </div>
              )}
            </div>
          </div>

          {/* Month sections — continuous vertical flow */}
          <div style={{ padding: "0 24px 80px", minWidth: calendarMinWidth }}>
            {months.map((month, mi) => (
              <MonthSection
                key={monthKey(month)}
                month={month}
                monthRef={mi === pastCount ? currentMonthRef : null}
                isCurrentMonth={mi === pastCount}
                {...cellProps}
              />
            ))}

            {isLoadingMore && (
              <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "#9ca3af" }}>
                Loading…
              </div>
            )}

            {/* Footer hint */}
            <div style={{
              marginTop: 6,
              paddingTop: 14,
              paddingBottom: 8,
              borderTop: "1px solid #f3f4f6",
            }}>
              {isAvMode ? (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    Double-click a day to add availability · Double-click a block to edit
                  </span>
                  <span style={{ display: "flex", gap: 5, alignItems: "center" }}>
                    <kbd style={CS.kbd}>⌘C</kbd>
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>copy</span>
                    <kbd style={CS.kbd}>⌘V</kbd>
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>paste</span>
                    <kbd style={CS.kbd}>Del</kbd>
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>delete</span>
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: 11, color: "#9ca3af" }}>
                  {isScheduledMode
                    ? "Scheduled hours computed from assigned sessions. Canceled sessions excluded."
                    : isStackedMode
                      ? "Click a day to view all segments."
                      : "Work time computed from payroll records."}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Availability modal ── */}
      <AvailabilityModal
        open={modalOpen}
        date={modalDate}
        mode={editBlock ? "edit" : "create"}
        initialStartMin={editBlock ? toDate(editBlock.start).getHours() * 60 + toDate(editBlock.start).getMinutes() : (modalBounds?.startMin ?? 9 * 60)}
        initialEndMin={editBlock   ? toDate(editBlock.end).getHours() * 60 + toDate(editBlock.end).getMinutes()   : (modalBounds?.endMin   ?? 17 * 60)}
        onClose={() => { setModalOpen(false); setEditBlock(null); setModalDate(null); }}
        onSave={saveModal}
        onDelete={deleteModal}
      />

      {/* ── Detail modal (teaching / admin day breakdown) ── */}
      {detailModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(15,23,42,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 999, padding: 20,
          }}
          onMouseDown={() => setDetailModal(null)}
        >
          <div
            style={{
              width: "min(400px,100%)",
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              boxShadow: "0 12px 40px rgba(15,23,42,0.18)",
              overflow: "hidden",
            }}
            onMouseDown={e => e.stopPropagation()}
          >
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", letterSpacing: "-0.2px" }}>
                {detailModal.date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                {MODE_META[detailModal.mode]?.label}
              </div>
            </div>
            <div style={{ padding: "12px 18px", display: "grid", gap: 6, maxHeight: "50vh", overflowY: "auto" }}>
              {detailModal.segs.map((seg, i) => (
                <div
                  key={`${seg.id}-${i}`}
                  style={{
                    borderRadius: 8,
                    border: `1px solid ${MODE_META[detailModal.mode]?.cardBorder || "#e5e7eb"}`,
                    background: MODE_META[detailModal.mode]?.cardBg || "#f9fafb",
                    color: MODE_META[detailModal.mode]?.cardText || "#374151",
                    padding: "8px 12px",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {fmtTime(seg.start)} – {fmtTime(seg.end)}
                </div>
              ))}
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setDetailModal(null)}
                style={{
                  padding: "7px 16px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontWeight: 500,
                  cursor: "pointer",
                  color: "#374151",
                  fontSize: 13,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// ─── Static styles ────────────────────────────────────────────────────────────

const CS = {
  selectWrap: {
    position: "relative",
    flexShrink: 0,
  },
  selectTrigger: {
    width: "100%",
    height: 32,
    padding: "0 11px",
    borderRadius: 9,
    border: "1px solid #e5e7eb",
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
    background: "#fff",
    outline: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    boxShadow: "0 1px 1px rgba(0,0,0,0.02)",
  },
  selectValue: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  selectMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
    padding: 6,
    zIndex: 60,
  },
  selectItem: {
    width: "100%",
    border: "none",
    background: "transparent",
    borderRadius: 7,
    padding: "8px 9px",
    fontSize: 13,
    fontWeight: 400,
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  todayBtn: {
    padding: "7px 14px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    flexShrink: 0,
  },
  kbd: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "1px 6px",
    fontSize: 10,
    fontFamily: "monospace",
    color: "#6b7280",
  },
};
